import { useAuth } from '@/core/contexts/AuthContext';
import { logger } from '@/core/logger';
import { navigateToWelcomeAfterLogout } from '@/core/navigation/logoutNavigation';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { useCallback, useState } from 'react';
import { profileService } from '../services/profileService';

/**
 * Hook for delete account business logic.
 * Handles API call, error states, and logout with redirect via getUnauthenticatedLandingPath.
 */
export const useDeleteAccount = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { logout } = useAuth();

    const deleteAccount = useCallback(async (): Promise<boolean> => {
        setIsLoading(true);
        setError(null);

        try {
            logger.info('useDeleteAccount:Calling delete-user API');

            trackDynatraceEvent({
                p_screen: 'profile',
                p_actionName: 'Delete Account Started',
                eventType: 'action',
            });
            await profileService.deleteUser();

            logger.info('useDeleteAccount:Account deleted successfully');

            trackDynatraceEvent({
                p_screen: 'profile',
                p_actionName: 'Delete Account Success',
                eventType: 'action',
            });
            // Logout then land on Universal or welcome per ENABLE_BLOOM_UNIVERSAL_LANDING
            await logout();
            await navigateToWelcomeAfterLogout();

            return true;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to delete account';
            setError(errorMessage);
            logger.error('useDeleteAccount:Failed to delete account', err);
            
            trackDynatraceEvent({
                p_screen: 'profile',
                p_actionName: 'Delete Account Failed',
                p_failedMessage: errorMessage,
                eventType: 'error',
            });
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [logout]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        deleteAccount,
        isLoading,
        error,
        clearError,
    };
};
